<template>
	<v-container fluid class="pa-0">
		<v-card class="pa-4 elevation-0" color="transparent">
			<!-- User header -->
			<v-sheet v-if="isCloud" class="pa-4 rounded-xl mb-4" color="white" elevation="1">
				<v-row align="center" no-gutters>
					<v-col cols="12" md="8" class="d-flex align-center">
						<v-avatar color="indigo lighten-4" size="56" class="ml-3">
							<v-icon color="indigo">mdi-wallet-bifold-outline</v-icon>
						</v-avatar>
						<div>
							<div class="text-h5">
								مانده اعتبار استعلام
							</div>
							<div class="grey--text">
								<strong>{{ appSettings.inquiryCredit }} تومان</strong>
							</div>
						</div>
					</v-col>
					<v-col cols="12" md="4" class="d-flex justify-end">
						<v-btn small color="indigo lighten-4" :loading="loadingUser" @click="addInquiryCreditCharge">
							<v-icon left small color="indigo">mdi-currency-usd</v-icon>
							<span class="indigo--text">
								افزایش اعتبار
							</span>
						</v-btn>
					</v-col>
				</v-row>
			</v-sheet>

			<!-- Loader / error -->
			<div v-if="loading">
				<v-progress-linear class="mt-3" color="primary" height="4" indeterminate rounded></v-progress-linear>
				<v-alert type="info" icon="mdi-timer-sand" outlined class="mt-2">
					در حال دریافت لیست استعلام‌ها...
				</v-alert>
			</div>
			<v-alert v-else-if="errorMessage" type="error" icon="mdi-alert-circle-outline" outlined>
				{{ errorMessage }}
			</v-alert>

			<!-- Empty state -->
			<v-alert v-else-if="!groups.length" type="warning" icon="mdi-information-outline" outlined>
				برای این شخص تاکنون استعلامی ثبت نشده است.
			</v-alert>

			<!-- Groups -->
			<v-row v-else dense>
				<v-col v-for="group in groups" :key="group.type" cols="12">
					<v-card class="rounded-xl" elevation="2">
						<v-card-title class="d-flex align-center">
							<v-avatar class="ml-3" size="40" :color="group.badgeColor || 'blue lighten-5'">
								<v-icon :color="group.iconColor || 'blue'">{{ group.icon || 'mdi-file-document-outline' }}</v-icon>
							</v-avatar>
							<div class="d-flex flex-column">
								<div class="text-subtitle-1 font-weight-bold d-flex">
									{{ group.title }}
									<v-tooltip top>
										<template v-slot:activator="{ on, attrs }">
											<v-icon small color="secondary" class="mr-1" v-bind="attrs" v-on="on">mdi-information-variant-circle-outline</v-icon>
										</template>
										<span>{{ group.desc }}</span>
									</v-tooltip>
								</div>
								<div class="grey--text">
									دفعات استعلام: <strong>{{ (group.items || []).length }}</strong>
								</div>
								<div class="grey--text" v-if="group.items.length">
									نتیجه آخرین استعلام:
									<strong>
										{{ group.items && group.items.length ? normalizeResult(group.items[group.items.length - 1].result) : '---' }}
									</strong>
								</div>
							</div>
							<v-spacer />
							<v-tooltip bottom>
								<template v-slot:activator="{ on, attrs }">
									<div class="text-center">
										<div v-if="isCloud" class="text-left grey--text mb-1">هزینه: {{ String(group.price).toPriceFormat() }} <small>تومان</small></div>
										<v-btn :disabled="!group.active" v-bind="attrs" v-on="on" small color="primary" :loading="submitInquiryLoading === group.type" class="rounded-lg" @click="submitInquiry(group.type)">
											<v-icon left small>mdi-plus</v-icon>
											استعلام جدید
										</v-btn>
									</div>
								</template>
								<span>ثبت استعلام جدید {{ group.title }}</span>
							</v-tooltip>
						</v-card-title>
						<v-divider />
						<v-card-text class="pa-0">
							<v-alert v-if="!group.items.length && isGroupExpanded(group.type)" type="warning" icon="mdi-information-outline" outlined class="ma-2">
								استعلامی یافت نشد.
							</v-alert>
							<v-list two-line dense class="py-0" v-show="group.items.length && isGroupExpanded(group.type)">
								<v-list-item v-for="(item, idx) in group.items" :key="idx" style="border-bottom: 1px dashed #eee;">
									<v-list-item-content>
										<v-list-item-title class="d-flex align-center justify-space-between">
											<span class="ml-2">&lrm;{{ item.dateTime }}</span>
											<v-chip small label class="ml-1" color="teal lighten-5" text-color="teal darken-2">
												<v-icon left x-small>mdi-currency-usd</v-icon>
												{{ formatPrice(item.cost) }} تومان
											</v-chip>
										</v-list-item-title>
										<v-list-item-subtitle class="d-flex align-center flex-wrap">
											<v-chip x-small class="ml-1 mb-1" color="indigo lighten-5" text-color="indigo darken-2" label>
												<v-icon left x-small>mdi-view-dashboard-outline</v-icon>
												{{ item.panelText || '---' }}
											</v-chip>
											<v-chip x-small class="ml-1 mb-1" :color="resultColor(item.result)" text-color="white" label>
												<v-icon left x-small>mdi-check-decagram</v-icon>
												{{ normalizeResult(item.result) }}
											</v-chip>
										</v-list-item-subtitle>
									</v-list-item-content>
								</v-list-item>
							</v-list>
							<div class="pa-3 text-center">
								<v-btn text small color="primary" @click="toggleGroup(group.type)" class="rounded-lg">
									<v-icon left small>
										{{ isGroupExpanded(group.type) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
									</v-icon>
									{{ isGroupExpanded(group.type) ? 'بستن لیست' : 'سابقه استعلام ها' }}
								</v-btn>
							</div>
						</v-card-text>
					</v-card>
				</v-col>
			</v-row>
		</v-card>

		<v-dialog v-model="dialogInquiryCreditCharge" scrollable persistent max-width="450px">
			<v-card>
				<v-card-title>
					<div><span class="headline">شارژ اعتبار پنل استعلام</span></div>
					<v-spacer />
					<v-btn @click="dialogInquiryCreditCharge = false" icon>
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-card-text>
					<v-container>
						<inquiry-credit-charge ref="inquiryCreditCharge" v-if="dialogInquiryCreditCharge" @callbck="callbck_inquiryCreditCharge" />
					</v-container>
				</v-card-text>
			</v-card>
		</v-dialog>

		<!-- confirm dialog placeholder in case we need later -->
		<confirm-dlg ref="confirm" />
	</v-container>
</template>

<script>
	import api from '@/util/app-api'
	import { call, get } from 'vuex-pathify'
	import toast from '@/util/toast'
	import { base_inquiries } from '@/plugins/inquiries.js'

	export default {
		name: 'UserInquiryHistory',
		props: {
			userId: { type: [String, Number], required: true },
			// Optional: if provided, we won't fetch user info
			userInfo: { type: Object, default: null },
			// Optional: override endpoints if differ per context
			endpoints: {
				type: Object,
				default: () => ({
					user: 'admin/Member/get/', // + {userId}
					inquiries: 'admin/AppInquiry/ListByMemberId/' // + {userId}
				})
			}
		},
		components: {
			ConfirmDlg: () => import('@/components/Confirm'),
			'inquiry-credit-charge': () => import('@/components/forms/InquiryCreditCharge.vue'),
		},
		data() {
			return {
				dialogInquiryCreditCharge: false,
				loading: false,
				loadingUser: false,
				appSettings: { dashboardSettings: {} },
				errorMessage: '',
				userInfoLocal: null,
				// groups: [{ type, title, icon, iconColor?, badgeColor?, active, items: [{ dateTime, cost, panel, result }] }]
				groups: [],
				expandedGroups: {}, // Track which groups are expanded
				submitInquiryLoading: ''
			}
		},
		computed: {
			...get('siteConfig', [
				'isCloud'
			]),
			userFullName() {
				const u = this.userInfoLocal
				if (!u) return ''
				return [u.firstName, u.lastName].filter(Boolean).join(' ')
			}
		},
		watch: {
			userId: {
				immediate: true,
				handler() {
					this.bootstrap()
				}
			},
			userInfo: {
				immediate: true,
				handler(val) {
					if (val) this.userInfoLocal = val
				}
			}
		},
		mounted() {
			this.loadAppSettings(false)
		},
		methods: {
			...call('appSettings/*'),
			loadAppSettings(force) {
				this.fetch({ force: force }).then(x => {
					this.appSettings = x
				})
			},
			async bootstrap() {
				this.errorMessage = ''
				if (!this.userInfo) {
					await this.loadUser()
				}
				await this.loadInquiries()
			},
			async reloadUser() {
				await this.loadUser(true)
			},
			async loadUser(showToast) {
				if (!this.userId) return
				this.loadingUser = true
				try {
					const response = await api.get(this.endpoints.user + this.userId, null)
					this.userInfoLocal = response
					if (showToast) toast.success('اطلاعات کاربر بروزرسانی شد.')
				} catch (err) {
					this.userInfoLocal = this.userInfoLocal || null
					if (showToast) toast.error(err.message)
				} finally {
					this.loadingUser = false
				}
			},
			async loadInquiries() {
				if (!this.userId) return
				this.loading = true
				this.groups = []
				try {
					// Expected response shapes supported:
					// 1) { groups: [...] }
					// 2) direct array [...]
					const response = await api.get(this.endpoints.inquiries + this.userId, null)
					const raw = Array.isArray(response) ? response : (response?.groups || [])
					this.groups = this.normalizeGroups(raw)
				} catch (err) {
					this.errorMessage = err.message
				} finally {
					this.loading = false
				}
			},
			normalizeGroups(rawGroups) {
				return base_inquiries.map(g => {
					const items = (rawGroups || []).filter(x => x.appInquiryType === g.type).map(it => ({
						dateTime: `${it.date_Persian.date} ${it.date_Persian.time}` || null,
						cost: it.cost ?? it.price ?? it.fee ?? 0,
						panel: it.inquiryPanel ?? it.source ?? it.provider ?? '',
						panelText: it.inquiryPanelText ?? '',
						result: it.result ?? it.status ?? '',
						parsedResult: it.panelResultJson ? JSON.parse(it.panelResultJson) : null
					}))
					return {
						type: g.type ?? g.type ?? g.code ?? g.title,
						title: g.title ?? this.fallbackTitle(g),
						desc: g.desc,
						icon: typeof (g.icon) === 'function' ? g.icon(items) : g.icon ?? this.detectIcon(g),
						iconColor: typeof (g.iconColor) === 'function' ? g.iconColor(items) : g.iconColor ?? undefined,
						badgeColor: typeof (g.badgeColor) === 'function' ? g.badgeColor(items) : g.badgeColor ?? undefined,
						active: Boolean(g.active ?? g.isActive ?? false),
						price: g.price,
						items
					}
				})
			},
			fallbackTitle(g) {
				const t = g?.type || g?.code || g?.type
				return t ? String(t) : 'نوع استعلام'
			},
			detectIcon(g) {
				const t = (g?.type || g?.code || g?.type || '').toString().toLowerCase()
				if (t.includes('shahkar') || t.includes('ability') || t.includes('beta')) return 'mdi-bank'
				if (t.includes('iban') || t.includes('sheba')) return 'mdi-bank-transfer'
				if (t.includes('identity') || t.includes('nid') || t.includes('national')) return 'mdi-card-account-details-outline'
				if (t.includes('criminal') || t.includes('judicial')) return 'mdi-scale-balance'
				if (t.includes('credit') || t.includes('score')) return 'mdi-star-check-outline'
				return 'mdi-file-document-outline'
			},
			formatPrice(val) {
				if (val === null || val === undefined || val === '') return '0'
				try {
					return String(Number(val)).toPriceFormat ? String(Number(val)).toPriceFormat() : Number(val).toLocaleString('fa-IR')
				} catch {
					return String(val)
				}
			},
			resultColor(res) {
				const r = (res || '').toString().toLowerCase()
				if (!r) return 'grey'
				if (['success', 'ok', 'موفق', 'تایید', 'مثبت', 'دارد'].some(k => r.includes(k))) return 'green'
				if (['fail', 'error', 'ناموفق', 'رد', 'منفی'].some(k => r.includes(k))) return 'red'
				return 'blue'
			},
			normalizeResult(res) {
				if (!res) return '---'
				return String(res)
			},
			isGroupExpanded(groupKey) {
				// Default to collapsed (false) if not set
				return this.expandedGroups[groupKey] === true
			},
			toggleGroup(groupKey) {
				// Toggle the expanded state for this group
				this.$set(this.expandedGroups, groupKey, !this.isGroupExpanded(groupKey))
			},
			async submitInquiry(type) {
				if (await this.$refs.confirm.open(
					'توجه',
					'آیا از ارسال درخواست استعلام مطمئن هستید؟',
				)) {
					let data = {
						inquiryType: type,
						memberId: this.userId
					}
					this.submitInquiryLoading = type
					api.post('admin/AppInquiry/InquiryRequest', data)
						.then(response => {
							toast.success('درخواست با موفقیت ثبت شد.')
							this.loadInquiries()
							this.loadAppSettings(true)
						})
						.catch(err => {
							toast.error(err.message)
						})
						.finally(() => {
							this.submitInquiryLoading = ''
						})
				}
			},
			addInquiryCreditCharge() {
				this.dialogInquiryCreditCharge = true
			},
			callbck_inquiryCreditCharge() {
				this.dialogInquiryCreditCharge = false
			}
		}
	}
</script>

<style scoped>

	/* Mobile-friendly tweaks */
	@media (max-width: 600px) {
		.v-card__title {
			padding: 12px 16px !important;
		}
	}
</style>
